import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, FormControl, Input, Text, Heading, Button, useToast, useColorModeValue,
  Flex, VStack, HStack, Select, Textarea, Switch, FormLabel,
} from "@chakra-ui/react";
import axios from "axios";

interface Blog {
  blog_id: number;
  title: string;
  slug: string;
  headline?: string;
  author_name?: string;
  description?: string;
  long_description?: string;
  published_at?: string;
  is_active: boolean;
  sequence?: number;
  blog_image_key?: string;
  card_image_key?: string;
  meta_title ?:string;
  meta_description ?:string;
  created_at?: string;
  updated_at?: string;
}

const B2CReviewBlog = () => {
  const cardbg = useColorModeValue("gray.50", "gray.700");
  const { blog_id } = useParams<{ blog_id: string }>();
  const [blogId, setBlogId] = useState<number>(Number(blog_id));
  const navigate = useNavigate();
  const toast = useToast();

  const [blog, setBlog] = useState<Blog>({
    blog_id: 0, title: '', slug: '', headline: '', author_name: '',
    description: '', long_description: '', published_at: '', is_active: true, sequence: 0, meta_description:'', meta_title:'' , 
  });

  const [isEdited, setIsEdited] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const isCreating = blogId === 0;
  const isViewMode = !isEditing;
  const showActionButtons = !isCreating;
  const showSaveButton = isCreating || !isViewMode;
  const isUpdateButtonEnabled = showActionButtons && isViewMode;

  const apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
  const token = localStorage.getItem("b2c_token");

  const fetchBlog = async () => {
    const { data } = await axios.get<Blog>(`${apiurl}/blog/${blog_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setBlog(data);
  };

  useEffect(() => {
    if (Number(blog_id) > 0) {
      fetchBlog();
      setBlogId(Number(blog_id));
    }
  }, [blog_id]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const finalValue = type === "checkbox" ? (e.target as HTMLInputElement).checked : value;
    setBlog(prev => ({ ...prev, [name]: finalValue }));
    setIsEdited(true);
  };

  const handleCreateBlog = async () => {
    try {
      const { data } = await axios.post<Blog>(`${apiurl}/create-blog/`, blog, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: "Blog created", status: "success" });
      setIsEdited(false);
      navigate(`/practice/app/b2c-blog-manager/${data.blog_id}/review-blog`);
    } catch (err: any) {
      toast({ title: err?.response?.data?.detail || "Error creating blog", status: "error" });
    }
  };

  const handleUpdateBlog = async () => {
    try {
      await axios.put(`${apiurl}/update-blog/${blog_id}`, blog, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: "Blog updated", status: "success" });
      setIsEdited(false);
      setIsEditing(false);
      fetchBlog();
    } catch (err: any) {
      toast({ title: err?.response?.data?.detail || "Error updating blog", status: "error" });
    }
  };

  const handleSaveBlog = () => {
    isCreating ? handleCreateBlog() : handleUpdateBlog();
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, imageType: "blog" | "card") => {
    const file = e.target.files?.[0];
    if (!file || isCreating) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${apiurl}/blog/${blog_id}/upload-image?image_type=${imageType}`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      toast({ title: "Image uploaded", status: "success" });
      fetchBlog();
    } catch (err: any) {
      toast({ title: "Image upload failed", status: "error" });
    }
  };

  return (
    <Box as="main" mx="auto" p={10}>
      <Box bg={useColorModeValue("gray.100", "gray.700")} p={4} borderRadius="sm" mb={4}>
        <Flex justify="space-between" align="center" wrap="wrap">
          <Heading size="lg">{blog.title || "Untitled Blog"}</Heading>
        </Flex>
      </Box>

      {showActionButtons && (
        <HStack spacing={2} mt={4} mb={4}>
          <Button size="sm" onClick={() => { setIsEditing(true); }} isDisabled={!isUpdateButtonEnabled} colorScheme="blue">
            Update Blog
          </Button>
          {isEditing && (
            <Button size="sm" variant="outline" colorScheme="red" onClick={() => { setIsEditing(false); fetchBlog(); }}>
              Cancel
            </Button>
          )}
        </HStack>
      )}

      <Box bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
        <fieldset disabled={!showSaveButton}>
          <Text mb={4} fontWeight="bold">Core Information</Text>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <Input name="title" placeholder="Blog Title*" size="sm" value={blog.title} onChange={handleInputChange} />
            </FormControl>
            <FormControl isRequired>
              <Input name="slug" placeholder="Slug*" size="sm" value={blog.slug} onChange={handleInputChange} />
            </FormControl>
            <FormControl>
              <Input name="headline" placeholder="Headline" size="sm" value={blog.headline || ""} onChange={handleInputChange} />
            </FormControl>
            <FormControl>
              <Input name="author_name" placeholder="Author Name" size="sm" value={blog.author_name || ""} onChange={handleInputChange} />
            </FormControl>
            <FormControl>
              <Input type="datetime-local" name="published_at" size="sm" value={blog.published_at?.slice(0, 16) || ""} onChange={handleInputChange} />
            </FormControl>
            <FormControl>
              <Textarea name="description" placeholder="Description (Markdown, shown on card)" size="sm" rows={4} value={blog.description || ""} onChange={handleInputChange} />
            </FormControl>
            <FormControl>
              <Textarea name="long_description" placeholder="Long Description (Markdown, shown on detail page)" size="sm" rows={8} value={blog.long_description || ""} onChange={handleInputChange} />
            </FormControl>
            <FormControl display="flex" alignItems="center">
              <FormLabel mb="0" fontSize="sm">Active</FormLabel>
              <Switch name="is_active" isChecked={blog.is_active} onChange={handleInputChange} />
            </FormControl>
          </VStack>
        </fieldset>
      </Box>

        {/* ===========================
            SEO SECTION
        =========================== */}
        <Box mt={4} bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
          <fieldset disabled={!showSaveButton}>
            <Text mb={4} fontSize="md" fontWeight="bold">
              SEO Metadata
            </Text>
      
            <VStack spacing={4} align="stretch">
      
              <FormControl id="meta_title">
                <Input
                  name="meta_title"
                  placeholder="Meta Title"
                  size="sm"
                  value={blog.meta_title || ""}
                  onChange={handleInputChange}
                />
              </FormControl>
      
              <FormControl id="meta_description">
                <Textarea
                  name="meta_description"
                  placeholder="Meta Description"
                  size="sm"
                  value={blog.meta_description || ""}
                  onChange={handleInputChange}
                />
              </FormControl>
      
            </VStack>
          </fieldset>
        </Box>

      {!isCreating && (
        <Box mt={4} bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
          <Text mb={4} fontWeight="bold">Images</Text>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Blog Detail Hero Image</FormLabel>
              <Input type="file" accept="image/*" size="sm" onChange={(e) => handleImageUpload(e, "blog")} />
              {blog.blog_image_key && <Text fontSize="xs" color="gray.500" mt={1}>Current: {blog.blog_image_key}</Text>}
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Card Thumbnail Image</FormLabel>
              <Input type="file" accept="image/*" size="sm" onChange={(e) => handleImageUpload(e, "card")} />
              {blog.card_image_key && <Text fontSize="xs" color="gray.500" mt={1}>Current: {blog.card_image_key}</Text>}
            </FormControl>
          </VStack>
        </Box>
      )}

      {showSaveButton && (
        <Box mt={4}>
          <Button colorScheme="teal" size="sm" onClick={handleSaveBlog} isDisabled={!isEdited && !isCreating}>
            Commit
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default B2CReviewBlog;